// AF-3.20.5.2 — Brand Overview executive Command Center.
//
// Answers "what is happening across my brand?" -- distinct from AI
// Discovery ("how does AI see my brand?"), Growth ("what should my
// brand do next?"), and Creator ("what should we create?"). This page
// performs NO client-side scoring, NO new objective/recommendation
// engine, and NO new backend call: every section reuses an existing,
// already-tested route also used elsewhere in the product --
// controlPlaneAPI (AF-3.14, same source as BrandCommandCenterPage),
// brandGrowthAPI.opportunities (AF-3.11, same source as
// OpportunityPortfolioPage), creatorCommandAPI (AF-3.10, same source as
// CreatorCommandCenterPage), and somDashboardAPI/intelligenceAPI.overview
// (same source as AIDiscoveryCommandCenterPage). "What changed" is
// assembled only from fields already present in those same responses --
// never a fabricated activity feed.

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { StatusBadge, GovernanceBadge, ConfidenceIndicator, ReadinessBadge } from '@/components/brand/Badges';
import { MetricCard } from '@/components/brand/MetricCard';
import { NextBestActionCard } from '@/components/brand/NextBestActionCard';
import { OpportunityCard } from '@/components/brand/OpportunityCard';
import { EvidenceDrawer } from '@/components/brand/EvidenceDrawer';
import { EmptyState, ErrorState, LoadingState, InsufficientEvidenceState } from '@/components/brand/StateViews';
import { useBrandContext } from '@/hooks/useBrandContext';
import { controlPlaneAPI, brandGrowthAPI, creatorCommandAPI } from '@/lib/intelligenceApi';
import { somDashboardAPI, intelligenceAPI } from '@/lib/api';
import {
  Binoculars, Target, VideoCamera, CaretRight, Warning, CheckCircle,
  ShieldWarning, Plugs, Compass, Eye, Lightbulb,
} from '@phosphor-icons/react';

function SectionLabel({ children }) {
  return <p className="text-[10px] font-mono uppercase tracking-[0.1em] text-[var(--text-tertiary)] mb-1.5">{children}</p>;
}

function CrossLink({ to, testId, icon: Icon, title, subtitle }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex items-center gap-3 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-3.5 shadow-[var(--shadow-card)] hover:border-[var(--accent-border)] hover:shadow-[var(--shadow-hover)] transition-all"
    >
      <div className="w-8 h-8 rounded-full bg-[var(--accent-light)] flex items-center justify-center shrink-0">
        <Icon size={16} className="text-[var(--accent)]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] text-[var(--text-primary)] font-semibold">{title}</p>
        <p className="text-[10px] text-[var(--text-tertiary)]">{subtitle}</p>
      </div>
      <CaretRight size={14} className="text-[var(--text-tertiary)] shrink-0" />
    </Link>
  );
}

// Phase 2 -- the one dedicated empty-workspace state: no brand/workspace
// connected at all yet. Explains the product workflow without implying
// any of it has already happened.
function EmptyWorkspaceOverview() {
  const steps = ['Connect', 'Understand', 'Discover', 'Act', 'Measure', 'Learn'];
  return (
    <div data-testid="overview-empty-workspace" className="flex flex-col items-center justify-center text-center py-16 px-6 rounded-[var(--radius-card)] border border-dashed border-[var(--border)] bg-[var(--surface-sunken)]">
      <div className="w-11 h-11 rounded-full bg-[var(--accent-light)] flex items-center justify-center mb-4">
        <Plugs size={22} className="text-[var(--accent)]" />
      </div>
      <p className="font-display text-[16px] font-bold text-[var(--text-primary)] mb-2">Connect your first data source to begin</p>
      <p className="text-[12px] text-[var(--text-secondary)] max-w-md mb-6">
        Martian builds its understanding of a brand entirely from connected, real evidence. Nothing below has happened yet -- this is the workflow once a source is connected.
      </p>
      <div className="flex items-center gap-1.5 flex-wrap justify-center mb-6">
        {steps.map((s, i) => (
          <span key={s} className="flex items-center gap-1.5">
            <span className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">{s}</span>
            {i < steps.length - 1 && <CaretRight size={10} className="text-[var(--text-placeholder)]" />}
          </span>
        ))}
      </div>
      <Link to="/onboarding" data-testid="overview-empty-cta" className="text-[12px] font-medium text-[var(--accent)]">
        Add a brand →
      </Link>
    </div>
  );
}

export default function BrandOverviewPage() {
  const ctx = useBrandContext();
  const [state, setState] = useState(null);
  const [stateLoading, setStateLoading] = useState(false);
  const [stateError, setStateError] = useState(null);

  const [som, setSom] = useState(null);
  const [aiOverview, setAiOverview] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const [oppView, setOppView] = useState(null);
  const [oppLoading, setOppLoading] = useState(false);

  const [command, setCommand] = useState(null);
  const [commandLoading, setCommandLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadState = useCallback(() => {
    if (!ctx.ready) return;
    setStateLoading(true); setStateError(null);
    controlPlaneAPI.create(ctx.workspaceId, { entity_type: ctx.entityType, entity_id: ctx.entityId })
      .then((r) => setState(r.data))
      .catch((e) => setStateError(e))
      .finally(() => setStateLoading(false));
  }, [ctx.ready, ctx.workspaceId, ctx.entityType, ctx.entityId]);

  useEffect(() => { loadState(); }, [loadState]);

  useEffect(() => {
    // P2.6 -- clear the previous brand's Share of Model / AI overview
    // before the new client's request resolves, so a switch can never
    // briefly show brand A's KPI under brand B (same P1.8/P1.9
    // stale-state discipline the other Growth pages already follow).
    setSom(null); setAiOverview(null);
    if (!ctx.workspaceId) return;
    setAiLoading(true);
    Promise.all([
      // P2.3 -- Share of Model scoped to the selected brand, not the newest
      // som_scores row across the whole account.
      somDashboardAPI.get(ctx.workspaceId).then((r) => r.data).catch(() => null),
      intelligenceAPI.overview(ctx.workspaceId).then((r) => r.data).catch(() => null),
    ]).then(([somData, overviewData]) => { setSom(somData); setAiOverview(overviewData); })
      .finally(() => setAiLoading(false));
  }, [ctx.workspaceId]);

  useEffect(() => {
    if (!ctx.ready) { setOppView(null); return; }
    setOppLoading(true);
    brandGrowthAPI.opportunities(ctx.workspaceId, ctx.entityType, ctx.entityId)
      .then((r) => setOppView(r.data))
      .catch(() => setOppView(null))
      .finally(() => setOppLoading(false));
  }, [ctx.ready, ctx.workspaceId, ctx.entityType, ctx.entityId]);

  useEffect(() => {
    if (!ctx.ready) { setCommand(null); return; }
    setCommandLoading(true);
    creatorCommandAPI.create(ctx.workspaceId, { entity_type: ctx.entityType, entity_id: ctx.entityId })
      .then((r) => setCommand(r.data))
      .catch(() => setCommand(null))
      .finally(() => setCommandLoading(false));
  }, [ctx.ready, ctx.workspaceId, ctx.entityType, ctx.entityId]);

  const objectiveResolved = state && state.primary_objective && state.primary_objective !== 'UNKNOWN';
  const somKpi = som?.kpi_cards?.som;
  const topOpportunities = oppView
    ? [...(oppView.top_opportunity_ids || []), ...(oppView.next_opportunity_ids || [])]
        .slice(0, 3)
        .map((id) => oppView.opportunities?.find((o) => o.id === id))
        .filter(Boolean)
    : [];

  // "What changed" -- assembled ONLY from fields already present in the
  // four responses above (no new call, no derived/invented signal).
  const changeSignals = [];
  const breached = state?.outcome_summary?.which_guardrails_moved?.breached || [];
  const clear = state?.outcome_summary?.which_guardrails_moved?.clear || [];
  if (breached.length > 0) changeSignals.push({ icon: Warning, tone: 'text-[var(--error)]', text: `Guardrail breached: ${breached.join(', ')}` });
  if (clear.length > 0) changeSignals.push({ icon: CheckCircle, tone: 'text-[var(--success)]', text: `Guardrail cleared: ${clear.join(', ')}` });
  if (state?.environment_risks?.length > 0) changeSignals.push({ icon: ShieldWarning, tone: 'text-[var(--warning)]', text: `${state.environment_risks.length} environment risk${state.environment_risks.length === 1 ? '' : 's'} recorded` });
  if (oppView?.top_opportunity_ids?.length > 0) changeSignals.push({ icon: Target, tone: 'text-[var(--accent)]', text: `${oppView.top_opportunity_ids.length} opportunit${oppView.top_opportunity_ids.length === 1 ? 'y' : 'ies'} ready now` });
  if (command?.opportunity_id && command?.current_stage) changeSignals.push({ icon: VideoCamera, tone: 'text-[var(--accent)]', text: `Creator pipeline at ${command.current_stage.replace(/_/g, ' ')}` });

  const noWorkspaceYet = !ctx.loadingClients && ctx.clients.length === 0;

  return (
    <DashboardLayout>
      <div data-testid="brand-overview-page" className="max-w-5xl mx-auto">
        <div className="mb-5">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Overview</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">What is happening across this brand, what changed, and what to do next.</p>
        </div>

        {noWorkspaceYet && <EmptyWorkspaceOverview />}

        {!noWorkspaceYet && (
          <>
            <BrandContextBar ctx={ctx} />

            {!ctx.ready && !ctx.loadingClients && (
              <EmptyState title="Select a workspace and entity" description="Choose a brand workspace and entity above to view its Overview." />
            )}

            {ctx.ready && (
              <>
                {/* 1. BRAND CONTEXT */}
                <SectionLabel>Brand context</SectionLabel>
                {stateLoading && <LoadingState />}
                {stateError && <ErrorState error={stateError} onRetry={loadState} />}
                {!stateLoading && !stateError && state && (
                  <div data-testid="overview-brand-context" className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 mb-5 shadow-[var(--shadow-card)] flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">{ctx.clients.find((c) => c.id === ctx.workspaceId)?.name || ctx.workspaceId}</p>
                      <p className="text-[14px] text-[var(--text-primary)] font-semibold">{ctx.selectedEntity?.canonical_name || ctx.entityId}</p>
                    </div>
                    <div className="flex items-center gap-5">
                      <div>
                        <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Brand state</p>
                        <StatusBadge value={state.status} />
                      </div>
                      <div>
                        <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Objective</p>
                        <p className="text-[12px] font-mono text-[var(--text-primary)]" data-testid="overview-objective-value">{state.primary_objective || 'UNKNOWN'}</p>
                      </div>
                    </div>
                  </div>
                )}
                {!stateLoading && !stateError && !objectiveResolved && state && (
                  <p className="text-[10px] text-[var(--text-tertiary)] mb-5 -mt-3" data-testid="overview-objective-unresolved-note">More evidence is required before Martian can establish a primary objective for this entity.</p>
                )}

                {/* 2. WHAT CHANGED */}
                <SectionLabel>What changed</SectionLabel>
                <div data-testid="overview-what-changed" className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 mb-5 shadow-[var(--shadow-card)]">
                  {changeSignals.length === 0 ? (
                    <p className="text-[12px] text-[var(--text-tertiary)] italic" data-testid="overview-no-changes">Nothing to report yet -- Martian has not recorded a guardrail move, new opportunity, environment risk, or production-stage change for this entity.</p>
                  ) : (
                    <ul className="space-y-2">
                      {changeSignals.map((c, i) => (
                        <li key={i} className="flex items-center gap-2 text-[12px]" data-testid="overview-change-row">
                          <c.icon size={14} className={`${c.tone} shrink-0`} />
                          <span className="text-[var(--text-primary)]">{c.text}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* 3. BRAND HEALTH / CONTROL STATE */}
                <SectionLabel>Brand health &amp; control state</SectionLabel>
                {objectiveResolved ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5" data-testid="overview-control-state">
                    <MetricCard
                      label={state.primary_metric}
                      baseline={state.baseline}
                      current={state.current_performance}
                      status={state.outcome_summary?.whether_conclusive === false ? 'INCONCLUSIVE' : state.control_status}
                    />
                    <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
                      <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-2">Confidence</p>
                      <ConfidenceIndicator value={state.confidence} />
                    </div>
                    <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
                      <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-2">Guardrails</p>
                      <p className="text-[11px] font-mono text-[var(--text-primary)]">{(state.guardrail_metrics || []).join(', ') || '—'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="mb-5">
                    <InsufficientEvidenceState entityLabel={ctx.selectedEntity?.canonical_name} testId="overview-control-state-insufficient" />
                  </div>
                )}

                {/* 4. AI DISCOVERY SNAPSHOT */}
                <SectionLabel>AI Discovery snapshot</SectionLabel>
                <div data-testid="overview-ai-discovery-snapshot" className="mb-5">
                  {aiLoading && <LoadingState />}
                  {!aiLoading && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                        <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
                          <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-2">Share of Model</p>
                          <p className="font-display text-[18px] font-semibold text-[var(--text-primary)]">{somKpi?.is_live ? somKpi.value : 'UNKNOWN'}</p>
                          {somKpi?.status && <div className="mt-1.5"><StatusBadge value={somKpi.status.toUpperCase()} /></div>}
                        </div>
                        <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
                          <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-2">Search Everywhere Score</p>
                          <p className="font-display text-[18px] font-semibold text-[var(--text-primary)]">{aiOverview?.search_everywhere_score?.score ?? aiOverview?.search_everywhere_score ?? 'UNKNOWN'}</p>
                        </div>
                      </div>
                      <CrossLink to="/ai-discovery" testId="overview-ai-discovery-link" icon={Binoculars} title="Open AI Discovery" subtitle="Full visibility, answers, competitors, and citations" />
                    </>
                  )}
                </div>

                {/* 5. GROWTH OPPORTUNITIES */}
                <SectionLabel>Growth opportunities</SectionLabel>
                <div data-testid="overview-growth-opportunities" className="mb-5">
                  {oppLoading && <LoadingState />}
                  {!oppLoading && topOpportunities.length === 0 && (
                    <EmptyState title="No opportunities yet" description="Martian has not identified any opportunities for this entity yet." />
                  )}
                  {!oppLoading && topOpportunities.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      {topOpportunities.map((opp) => (
                        <OpportunityCard key={opp.id} opportunity={opp} classification={oppView.classifications?.[opp.id]} />
                      ))}
                    </div>
                  )}
                  <Link to="/growth/opportunities" data-testid="overview-opportunities-link" className="text-[11px] font-medium text-[var(--accent)] inline-block">View all opportunities →</Link>
                </div>

                {/* 6. CREATOR PIPELINE */}
                <SectionLabel>Creator pipeline</SectionLabel>
                <div data-testid="overview-creator-pipeline" className="mb-5">
                  {commandLoading && <LoadingState />}
                  {!commandLoading && (!command || !command.opportunity_id) && (
                    <EmptyState title="No production in progress" description="Martian has not started a creative production pipeline for this entity yet." />
                  )}
                  {!commandLoading && command?.opportunity_id && (
                    <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 mb-3 shadow-[var(--shadow-card)]">
                      <div className="flex items-center gap-1.5 flex-wrap mb-2">
                        {['Strategy', 'Ideas', 'Review', 'Decisions', 'Production', 'Distribution'].map((stage, i, arr) => (
                          <span key={stage} className="flex items-center gap-1.5">
                            <span className={`text-[10px] font-mono uppercase ${command.current_stage?.replace(/_/g, '')?.toLowerCase() === stage.toLowerCase() ? 'text-[var(--accent)] font-medium' : 'text-[var(--text-tertiary)]'}`}>{stage}</span>
                            {i < arr.length - 1 && <CaretRight size={10} className="text-[var(--text-tertiary)]" />}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusBadge value={command.command_status} />
                        <ReadinessBadge value={command.readiness} />
                        {command.distribution_status === 'READY' && (
                          <span className="text-[10px] text-[var(--text-secondary)]" data-testid="overview-ready-for-authorized-distribution">READY FOR AUTHORIZED DISTRIBUTION EXECUTION</span>
                        )}
                      </div>
                    </div>
                  )}
                  <CrossLink to="/growth/creator-command-center" testId="overview-creator-link" icon={VideoCamera} title="Open Creator Command Center" subtitle="Full Strategy → Ideas → Review → Decisions → Production → Distribution pipeline" />
                </div>

                {/* 7. PRIMARY NEXT ACTION -- the dominant element */}
                <SectionLabel>Primary next action</SectionLabel>
                <div className="mb-5" data-testid="overview-next-action">
                  {objectiveResolved ? (
                    <NextBestActionCard
                      action={state.next_best_action}
                      reason={state.primary_control_decision?.reason}
                      readiness={state.primary_control_decision?.readiness}
                      blockers={state.primary_control_decision?.blockers}
                      onViewEvidence={() => setDrawerOpen(true)}
                    />
                  ) : (
                    <p className="text-[11px] text-[var(--text-tertiary)] italic">No next action until a primary objective is established.</p>
                  )}
                </div>

                {/* 8. EVIDENCE / TRUST */}
                <SectionLabel>Evidence &amp; trust</SectionLabel>
                <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)] flex items-center justify-between flex-wrap gap-3" data-testid="overview-evidence-trust">
                  <div className="flex items-center gap-5 flex-wrap">
                    <div>
                      <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Evidence</p>
                      <p className="text-[11px] font-mono text-[var(--text-primary)]">{(state?.evidence || []).length} item{(state?.evidence || []).length === 1 ? '' : 's'}</p>
                    </div>
                    <div>
                      <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Governance</p>
                      {state?.environment_risks?.[0]?.state ? <GovernanceBadge value={state.environment_risks[0].state} /> : <GovernanceBadge value={null} />}
                    </div>
                    <div>
                      <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Causality</p>
                      <p className="text-[11px] font-mono text-[var(--text-primary)]" data-testid="overview-causality-value">{state?.causality || 'NOT_PROVEN'}</p>
                    </div>
                  </div>
                  <button data-testid="overview-view-evidence-button" onClick={() => setDrawerOpen(true)} className="flex items-center gap-1 text-[11px] text-[var(--accent)] font-medium">
                    <Lightbulb size={13} /> Why does Martian believe this? <CaretRight size={12} />
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {state && (
          <EvidenceDrawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            title="Why Martian assigned this state"
            evidence={state.evidence}
            provenance={state.provenance}
            unknowns={state.narrative?.what_we_do_not_know}
            causality={state.causality}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
